import React from 'react';
import { User, Loader2, Sparkles } from 'lucide-react';
import DatePicker from '@components/common/DatePicker';
import TimePicker from '@components/common/TimePicker';
import LocationPicker from '@components/common/LocationPicker';
import Select from '@components/common/Select';
import { FREE_KUNDLI_TRANSLATIONS } from '../../../../languages/freeKundli';

interface KundliFormProps {
    formData: any;
    setFormData: (data: any) => void;
    handleGenerate: () => void;
    handleClear: () => void;
    loading: boolean;
    language: 'english' | 'hindi';
}

const KundliForm: React.FC<KundliFormProps> = ({ formData, setFormData, handleGenerate, handleClear, loading, language }) => {
    const langKey = language === 'hindi' ? 'Hindi' : 'English';
    const t = FREE_KUNDLI_TRANSLATIONS[langKey];

    return (
        <div className="bg-white/[0.08] backdrop-blur-2xl border border-white/20 rounded-[24px] md:rounded-[32px] p-5 md:p-12 shadow-2xl mb-10 md:mb-16 relative group overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-primary-600/5 blur-3xl -mr-32 -mt-32 transition-colors group-hover:bg-primary-600/10" />

            <div className="space-y-8 relative z-10">
                {/* Row 1: Full Name and Gender */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-3 ml-1">{t.fullName}</label>
                        <div className="relative">
                            <div className="absolute left-4 top-1/2 -translate-y-1/2 text-primary-400">
                                <User size={18} />
                            </div>
                            <input
                                type="text"
                                placeholder={t.fullNamePlaceholder}
                                className="w-full bg-white/5 border border-white/20 rounded-2xl py-3 md:py-4 pl-12 pr-4 text-white focus:outline-none focus:ring-2 focus:ring-primary-500/50 transition-colors placeholder:text-white/20 font-sans text-sm md:text-base"
                                value={formData.name}
                                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                            />
                        </div>
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-3 ml-1">{t.gender}</label>
                        <Select
                            value={formData.gender}
                            onChange={(val) => setFormData({ ...formData, gender: val })}
                            options={[
                                { value: 'Male', label: t.male },
                                { value: 'Female', label: t.female },
                                { value: 'Other', label: t.other }
                            ]}
                        />
                    </div>
                </div>

                {/* Row 2: Date and Time */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-3 ml-1">{t.dateOfBirth}</label>
                        <DatePicker value={formData.dob} onChange={(val) => setFormData({ ...formData, dob: val })} />
                    </div>
                    <div>
                        <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-3 ml-1">{t.timeOfBirth}</label>
                        <TimePicker value={formData.birth_time} onChange={(val) => setFormData({ ...formData, birth_time: val })} />
                    </div>
                </div>

                {/* Row 3: Location */}
                <div>
                    <label className="block text-xs font-bold text-primary-300 uppercase tracking-widest mb-3 ml-1">{t.birthLocation}</label>
                    <LocationPicker
                        value={formData.location}
                        placeholder={t.searchCity}
                        onChange={(loc) => setFormData({
                            ...formData,
                            location: loc.name,
                            latitude: loc.latitude,
                            longitude: loc.longitude
                        })}
                    />
                </div>
            </div>

            <div className="mt-12 text-center relative z-10 flex flex-col items-center justify-center gap-3">
                <button
                    onClick={handleGenerate}
                    disabled={loading}
                    className="group relative px-8 md:px-12 py-4 md:py-5 bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-500 hover:to-secondary-500 text-white font-black rounded-2xl transition-colors shadow-[0_10px_30px_rgba(139,92,246,0.3)] hover:shadow-[0_15px_40px_rgba(139,92,246,0.4)] disabled:opacity-50 flex items-center gap-3 overflow-hidden w-full md:w-auto md:min-w-[300px]"
                >
                    <div className="absolute inset-0 bg-white/20 translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
                    {loading ? (
                        <div className="flex items-center gap-3 mx-auto">
                            <Loader2 className="w-5 h-5 animate-spin" />
                            <span>{t.readingStars}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-3 mx-auto">
                            <span className="uppercase tracking-widest">{t.generateFreeKundli}</span>
                        </div>
                    )}
                </button>
                <button
                    onClick={handleClear}
                    className="text-primary-300/60 hover:text-primary-300 text-sm font-medium transition-colors underline underline-offset-2"
                >
                    {t.clearForm}
                </button>
            </div>
        </div>
    );
};

export default KundliForm;
